import React, { useState, useEffect, FormEvent } from 'react';
import { LogIn, Mail, Lock, X, ChevronDown } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          nonce: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
  error?: string;
}

interface LoginProps {
  onClose?: () => void;
}

const Login: React.FC<LoginProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [isMagicLinkPending, setIsMagicLinkPending] = useState(false);

  const { login, signup, loginWithGoogle, loginWithApple, resendVerification, requestMagicLink, pendingVerificationEmail, clearPendingVerification } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (useMagicLink) {
        await requestMagicLink(email);
        setIsMagicLinkPending(true);
        setShowVerificationPending(true);
        return;
      } else if (isSignup) {
        const result = await signup(email, password);
        if (result.needsVerification) {
          setShowVerificationPending(true);
          return;
        }
      } else {
        await login(email, password);
      }
      // Close modal on successful login
      if (onClose) onClose();
    } catch (err) {
      // Check for email not verified error
      const authError = err as { code?: string; message?: string };
      if (authError.code === 'EMAIL_NOT_VERIFIED') {
        setShowVerificationPending(true);
      } else {
        setError(authError.message || 'Failed to authenticate');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setLoading(true);
    setVerificationResent(false);

    try {
      if (isMagicLinkPending) {
        await requestMagicLink(pendingVerificationEmail || email);
      } else {
        await resendVerification(pendingVerificationEmail || email);
      }
      setVerificationResent(true);
    } catch (err) {
      const authError = err as { message?: string };
      setError(authError.message || (isMagicLinkPending ? 'Failed to resend sign in link' : 'Failed to resend verification email'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerificationPending(false);
    setVerificationResent(false);
    setIsMagicLinkPending(false);
    clearPendingVerification();
  };

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    setError('');
    setLoading(true);

    try {
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }
      await loginWithGoogle({ credential: credentialResponse.credential });
      // Close modal on successful login
      if (onClose) onClose();
    } catch (err) {
      const authError = err as { message?: string };
      setError(authError.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Failed to sign in with Google');
  };

  // Initialize Apple Sign In SDK
  useEffect(() => {
    if (!process.env.REACT_APP_APPLE_CLIENT_ID) return;

    // Load Apple's SDK
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID!,
          scope: 'name email',
          redirectURI: window.location.origin,
          state: 'state',
          nonce: 'nonce',
          usePopup: true, // Use popup instead of redirect to avoid 405 error
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleAppleSignIn = async () => {
    if (!window.AppleID) {
      setError('Apple Sign In not loaded');
      return;
    }

    try {
      const response = await window.AppleID.auth.signIn();
      await loginWithApple(response);
      if (onClose) onClose();
    } catch (error) {
      console.error('Apple Sign In error:', error);
      const appleError = error as { error?: string };
      if (appleError.error !== 'popup_closed_by_user') {
        setError('Failed to sign in with Apple');
      }
    }
  };

  // Verification pending view
  if (showVerificationPending) {
    return (
      <div className={onClose ? '' : 'min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center px-4'}>
        <div className="max-w-md w-full">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-700 relative">
            {onClose && (
              <button
                onClick={() => {
                  handleBackToLogin();
                  onClose();
                }}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <div className="flex justify-center mb-6">
              <Mail className="w-16 h-16 text-blue-400" />
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-4">
              Check your email
            </h2>

            <p className="text-gray-300 text-center mb-2">
              {isMagicLinkPending ? 'We sent a sign in link to:' : 'We sent a verification link to:'}
            </p>

            <p className="text-blue-400 font-semibold text-center mb-4">
              {pendingVerificationEmail || email}
            </p>

            <p className="text-gray-400 text-sm text-center mb-6">
              {isMagicLinkPending
                ? 'Click the link in the email to sign in. The link expires in 15 minutes.'
                : 'Click the link in the email to verify your account and sign in.'}
            </p>

            {verificationResent && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 text-sm text-center">
                {isMagicLinkPending ? 'Sign in link resent!' : 'Verification email resent!'}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors py-2 disabled:opacity-50"
            >
              {loading ? 'Sending...' : (isMagicLinkPending ? 'Resend sign in link' : 'Resend verification email')}
            </button>

            <button
              onClick={handleBackToLogin}
              className="w-full text-gray-400 hover:text-gray-300 text-sm transition-colors py-2 mt-2"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={onClose ? '' : 'min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center px-4'}>
      <div className="max-w-md w-full">
        {!onClose && (
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-3">
              Service Timer
            </h1>
            <p className="text-gray-400 text-lg">Sign in to save your timer settings</p>
          </div>
        )}

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-700 relative">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <div className="flex justify-center mb-6">
            <LogIn className="w-12 h-12 text-blue-400" />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* OAuth Buttons - Prominent */}
          <div className="space-y-3">
            {/* Apple Sign In Button */}
            {process.env.REACT_APP_APPLE_CLIENT_ID ? (
              <button
                onClick={handleAppleSignIn}
                disabled={loading}
                className="w-full bg-black hover:bg-gray-900 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 border border-gray-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </button>
            ) : (
              <div className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-3 text-center text-gray-400 text-sm">
                Apple Sign In not configured
              </div>
            )}

            {/* Google Sign In Button - Always show, move outside conditional */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={handleGoogleError}
                useOneTap
                theme="filled_blue"
                size="large"
                text="continue_with"
                shape="rectangular"
                width="384"
              />
            </div>
          </div>

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-600"></div>
            <span className="px-4 text-gray-400 text-sm">or</span>
            <div className="flex-1 border-t border-gray-600"></div>
          </div>

          {/* Collapsible Email/Password Form */}
          <div>
            <button
              type="button"
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="w-full text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors py-2 flex items-center justify-center gap-2"
            >
              Sign in with email
              <ChevronDown className={`w-4 h-4 transition-transform ${showEmailForm ? 'rotate-180' : ''}`} />
            </button>

            {showEmailForm && (
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                {/* Password field - only show when not using magic link */}
                {!useMagicLink && (
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-gray-700/50 border border-gray-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || (!useMagicLink && !password)}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? 'Please wait...' : (useMagicLink ? 'Send Sign In Link' : (isSignup ? 'Sign Up' : 'Sign In'))}
                </button>

                {/* Toggle between password and magic link */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setUseMagicLink(!useMagicLink);
                      setError('');
                    }}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  >
                    {useMagicLink ? 'Use password instead' : 'Sign in with email link'}
                  </button>
                </div>

                {/* Toggle between sign in and sign up - only show for password mode */}
                {!useMagicLink && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setIsSignup(!isSignup)}
                      className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                    >
                      {isSignup ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>Your timer settings will be saved securely in the cloud</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
